import {
  EXPERIENCE_ID,
  MapExperience,
  MapCardMarker,
  MapIconMarker,
  MapLocation,
  MapItinerary,
  GoogleDirectionResult,
  MapGeoJSON,
} from "types/map";
import { getNeutralExperience } from "libraries/neutral";
import { flow, filter, first, get, pick } from "lodash/fp";
import experiences from "json/experiences.json";
import {
  MAP_MARKERS_PNG_CARDS_BASE,
  MAP_MARKERS_SVG_ICONS_BASE,
  MAP_MARKERS_SVG_PRODUCTS_BASE,
} from "libraries/svgPaths";

export const neutralMapLocation = (): MapLocation => ({
  formattedAddress: "",
  latitude: null,
  longitude: null,
  placeName: undefined,
  placeTypes: undefined,
});

export const hasLatLong = ({ latitude, longitude }: MapLocation) =>
  Boolean(latitude && longitude);

const PLACE_TYPE_NOISE = new Set([
  "geocode",
  "political",
  "establishment",
  "point_of_interest",
  "premise",
  "subpremise",
  "plus_code",
  "route",
  "street_address",
]);

const PLACE_TYPE_LABELS: Record<string, string> = {
  airport: "Airport",
  lodging: "Hotel",
  restaurant: "Restaurant",
  cafe: "Cafe",
  bar: "Bar",
  museum: "Museum",
  park: "Park",
  shopping_mall: "Shopping",
  store: "Store",
  supermarket: "Supermarket",
  train_station: "Train station",
  subway_station: "Subway",
  transit_station: "Transit",
  bus_station: "Bus station",
  taxi_stand: "Taxi",
  gas_station: "Gas station",
  parking: "Parking",
  hospital: "Hospital",
  school: "School",
  university: "University",
  locality: "City",
  postal_town: "City",
  administrative_area_level_1: "Region",
  administrative_area_level_2: "Area",
  country: "Country",
  neighborhood: "Neighborhood",
  tourist_attraction: "Attraction",
  stadium: "Stadium",
  gym: "Gym",
  spa: "Spa",
  bakery: "Bakery",
  meal_takeaway: "Food",
  meal_delivery: "Food",
  movie_theater: "Cinema",
  campground: "Camping",
  rv_park: "RV park",
  art_gallery: "Gallery",
  amusement_park: "Amusement park",
  zoo: "Zoo",
  aquarium: "Aquarium",
  night_club: "Nightlife",
  casino: "Casino",
  department_store: "Shopping",
  clothing_store: "Shopping",
};

/** Human label from Google Places types (skips noisy types). */
export const getPlaceTypeLabel = (
  placeTypes?: string[]
): string | undefined => {
  if (!placeTypes?.length) {
    return undefined;
  }

  for (const type of placeTypes) {
    if (PLACE_TYPE_NOISE.has(type)) {
      continue;
    }
    if (PLACE_TYPE_LABELS[type]) {
      return PLACE_TYPE_LABELS[type];
    }
    return type
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  return undefined;
};

/** Primary display title for a stop card / marker. */
export const getLocationDisplayTitle = (location: MapLocation): string => {
  const name = location.placeName?.trim();
  const address = location.formattedAddress?.trim();

  if (name && address && name !== address) {
    return name;
  }

  return name || address || "Unknown location";
};

export const buildItineraryRouteKey = (
  from: MapLocation,
  destinations: MapLocation[],
  travelMode?: string,
  legModes: string[] = []
): string =>
  [
    travelMode ?? "",
    from.formattedAddress ?? "",
    from.latitude ?? "",
    from.longitude ?? "",
    ...destinations.flatMap((destination) => [
      destination.formattedAddress ?? "",
      destination.latitude ?? "",
      destination.longitude ?? "",
    ]),
    ...legModes,
  ].join("|");

export const toGoogleLatLong = (
  latitude: number | null,
  longitude: number | null
) => ({
  lat: latitude ?? 0,
  lng: longitude ?? 0,
});

const sumLegValues = (
  legs: google.maps.DirectionsLeg[] | undefined,
  field: "distance" | "duration"
): number =>
  (legs ?? []).reduce(
    (total, leg) => total + (leg[field]?.value ?? 0),
    0
  );

const formatTotalDistanceLabel = (meters: number): string => {
  if (meters >= 1000) {
    return `${Math.round(meters / 1000)} km`;
  }

  return `${meters} m`;
};

export const mapDirectionResultParser = (
  directionResult: GoogleDirectionResult | null
) => {
  if (!directionResult) {
    console.error("[Library] mapDirectionResultParser - result empty");
    return {
      distanceMeters: 0,
      distanceLabel: "",
      durationSeconds: 0,
      googleDirection: null,
      bounds: null,
    };
  }

  return flow(
    get("routes[0]"),
    pick(["legs", "bounds"]),
    ({ legs, bounds }): Omit<MapItinerary, "locationFrom" | "locationTo"> => {
      const distanceMeters = sumLegValues(legs, "distance");
      const durationSeconds = sumLegValues(legs, "duration");

      return {
        distanceMeters,
        distanceLabel: formatTotalDistanceLabel(distanceMeters),
        durationSeconds,
        googleDirection: directionResult,
        bounds,
      };
    }
  )(directionResult);
};

export const getExperienceById = (experienceId: EXPERIENCE_ID): MapExperience =>
  flow(
    filter(["experienceId", experienceId]),
    first,
    (x: MapExperience | undefined): MapExperience => x ?? getNeutralExperience()
  )(experiences);

export const getMarkerCardImageSrc = ({ cardId }: MapCardMarker): string =>
  `${MAP_MARKERS_PNG_CARDS_BASE}${cardId}.png`;

export const getMarkerIconImageSrc = ({
  experienceId,
}: MapIconMarker): string => `${MAP_MARKERS_SVG_ICONS_BASE}${experienceId}.svg`;

const PRODUCT_ICONS: Record<string, string> = {
  "Açái": "Acai.svg",
  "Médio Juruá Açaí": "Acai.svg",
  "Rubber": "Rubber.svg",
  "Médio Juruá Rubber": "Rubber.svg",
  "Xingu Rubber": "Rubber.svg",
  "Handicrafts": "Crafts.svg",
  "Indigenous Handicrafts": "Crafts.svg",
  "Xingu Handicrafts": "Crafts.svg",
  "Yanomami Cocoa": "Cocoa.svg",
  "Forest agro-biodiversity products / Public food programmes (PNAE/PAA)": "AgroforestryFood Programmes.svg",
  "Agroforestry & Food Programmes": "AgroforestryFood Programmes.svg",
  "Amapá Agroforestry": "AgroforestryFood Programmes.svg",
  "Forest agro-biodiversity products (PNAE/PAA)": "AgroforestryFood Programmes.svg",
  "Mushroom": "Mushrooms.svg",
  "Brazil Nuts": "Brazil Nut.svg",
  "Amapá Brazil Nuts": "Brazil Nut.svg",
  "Xingu Brazil Nuts": "Brazil Nut.svg",
  "Manioc Flour": "Manioc Flour.svg",
  "Amapá Manioc Flour": "Manioc Flour.svg",
  "Biocosmetics": "Bio Cosmetics.svg",
  "Amapá Biocosmetics": "Bio Cosmetics.svg",
  "Xingu Biocosmetics": "Bio Cosmetics.svg",
  "Médio Juruá Seed Oils & Butters": "Murumuru.svg",
  "Seed oils (biocosmetics)": "Bio Cosmetics.svg",
  "Native seeds": "Maikîn.svg",
  "Xingu Native Seeds": "Maikîn.svg",
  "Honey": "Honey.svg",
  "Amapá Honey": "Honey.svg",
};

export const getProductIconSrc = (productTag: string): string | null => {
  const iconName = PRODUCT_ICONS[productTag];
  return iconName ? `${MAP_MARKERS_SVG_PRODUCTS_BASE}${iconName}` : null;
};

export const getCardHref = ({ cardId }: MapCardMarker) =>
  `${import.meta.env.REACT_APP_CARD_BASE_URL}${cardId}`;

export const mapEmbedCardMarkerNavigate = (cardMarker: MapCardMarker) => {
  if (!cardMarker.cardId) {
    console.warn(`[Map] Navigate to card with no id`);
  }

  if (cardMarker.embedDirectLink) {
    console.log(
      `[Map] Navigate to direct link [${cardMarker.embedDirectLink}]`
    );
    window.open(cardMarker.embedDirectLink, "_blank");
  } else {
    console.log(`[Map] Navigate to card [${cardMarker.cardId}]`);
    window.open(getCardHref(cardMarker), "_blank");
  }
};

export const calculateGeoJSONArea = (geoJSON: MapGeoJSON): number => {
  const EARTH_RADIUS = 6378137;
  const SQ_METERS_TO_SQ_FEET = 10.7639;

  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const calculateRingArea = (coordinates: number[][]): number => {
    let area = 0;
    if (coordinates.length > 2) {
      for (let i = 0; i < coordinates.length; i++) {
        const [lng1, lat1] = coordinates[i];
        const [lng2, lat2] = coordinates[(i + 1) % coordinates.length];
        area +=
          toRadians(lng2 - lng1) *
          (2 + Math.sin(toRadians(lat1)) + Math.sin(toRadians(lat2)));
      }
      area = (area * EARTH_RADIUS * EARTH_RADIUS) / 2;
    }
    return Math.abs(area);
  };

  let totalAreaSqMeters = 0;

  geoJSON.features.forEach((feature) => {
    const geometry = feature.geometry;
    if (geometry.type === "Polygon") {
      const rings = geometry.coordinates;
      if (rings.length > 0) {
        // Outer ring
        let polygonArea = calculateRingArea(rings[0]);
        // Inner rings (holes)
        for (let i = 1; i < rings.length; i++) {
          polygonArea -= calculateRingArea(rings[i]);
        }
        totalAreaSqMeters += polygonArea;
      }
    } else if (geometry.type === "MultiPolygon") {
      const polygons = geometry.coordinates;
      polygons.forEach((rings: number[][][]) => {
        if (rings.length > 0) {
          let polygonArea = calculateRingArea(rings[0]);
          for (let i = 1; i < rings.length; i++) {
            polygonArea -= calculateRingArea(rings[i]);
          }
          totalAreaSqMeters += polygonArea;
        }
      });
    }
  });

  return totalAreaSqMeters * SQ_METERS_TO_SQ_FEET;
};

export const formatSqFtToKm2 = (sqFt: number): string => {
  const SQ_METERS_TO_SQ_FEET = 10.7639;
  const km2 = Math.round(sqFt / SQ_METERS_TO_SQ_FEET / 1000000);
  return `${km2.toLocaleString()} km²`;
};
